/**
 * The Registration tab's screens share one scrolling body, so swapping screens kept the old scroll offset: after
 * founding, the Corporation screen opened scrolled past its header to the P&L (D-459 nit; the founding form had been
 * scrolled down to its Register button). `createScreenScroll` puts every scrolling ancestor back at the top when the
 * shown screen changes, and leaves it alone on re-renders of the same screen. UI events only.
 */

import type { CorpView } from '../../strategic/types'
import type { StationsPage } from './stationsHost'

/** Which Registration screen shows: the loading line, the founding form, a station page or the Corporation screen. */
export function screenKey(view: Pick<CorpView, 'founded'> | null, page: StationsPage | null): string {
  if (view === null) return 'loading'
  if (!view.founded) return 'founding'
  if (page !== null) return page.kind === 'manage' ? `manage:${page.id}` : page.kind
  return 'corp'
}

/** The part of an element this needs (an `HTMLElement` is one). */
export interface ScrollNode {
  scrollTop: number
  readonly parentElement: ScrollNode | null
}

/** Scrolls `el` and every ancestor back to the top. */
export function scrollToTop(el: ScrollNode | null): void {
  for (let n = el; n !== null; n = n.parentElement) if (n.scrollTop !== 0) n.scrollTop = 0
}

/** Returns `shown(key, el)`: call after each render with the shown screen's key; a new key scrolls to the top. */
export function createScreenScroll(): (key: string, el: ScrollNode | null) => void {
  let last: string | null = null
  return (key, el) => {
    if (key === last) return
    last = key
    scrollToTop(el)
  }
}
